import { useMutation, useQuery } from '@apollo/client';
import { useCallback, useEffect, useState } from 'react';
import {
  GET_EDITOR_PREFERENCE,
  SET_EDITOR_PREFERENCE,
} from './graphql/settings';

// Which editor the publish page opens with.
//
// Three states, not two: `useEditorV2` is null until the author has actually
// chosen. New signups are written as `true` at account creation, so a null here
// means an account that predates Editor v2 — Classic until they opt in. JP's
// rule is that Classic stays available forever, so nothing here ever migrates
// anyone silently.
//
// A local mirror answers on the first paint (the query is a network round trip
// and the editor mounts immediately) and `?editor=v2` / `?editor=classic` still
// overrides both, which is how this is testable without an account.

const LOCAL_KEY = 'tf_editor_v2';

const readLocal = () => {
  if (typeof window === 'undefined') return null;
  try {
    const stored = window.localStorage.getItem(LOCAL_KEY);
    return stored === null ? null : stored === '1';
  } catch {
    // Private mode. The server preference still governs once it arrives.
    return null;
  }
};

const writeLocal = value => {
  try {
    window.localStorage.setItem(LOCAL_KEY, value ? '1' : '0');
  } catch {
    // Nothing to do — the server copy is the real one.
  }
};

const readOverride = () => {
  if (typeof window === 'undefined') return null;
  const param = new URLSearchParams(window.location.search).get('editor');
  if (param === 'v2') return true;
  if (param === 'classic') return false;
  return null;
};

export default function useEditorPreference() {
  const { data, loading, refetch } = useQuery(GET_EDITOR_PREFERENCE, {
    fetchPolicy: 'cache-and-network',
    ssr: false,
  });
  const [save] = useMutation(SET_EDITOR_PREFERENCE);
  const [override, setOverride] = useState(null);
  const [local, setLocal] = useState(null);

  // localStorage and the query string are only readable after mount; reading
  // them during render would make the server and client markup disagree.
  useEffect(() => {
    const fromUrl = readOverride();
    setOverride(fromUrl);
    if (fromUrl !== null) writeLocal(fromUrl);
    setLocal(fromUrl !== null ? fromUrl : readLocal());
  }, []);

  const server = data && data.preferences ? data.preferences.useEditorV2 : null;

  // Mirror the server's answer as soon as it arrives, not only when the author
  // uses the toggle. Every signup is written `true` at account creation, so an
  // author who has never touched the switch had no local answer at all — and
  // on a device with no mirror the editor mounts Classic first and swaps to v2
  // when the query lands. Writing it here means that happens once per device
  // rather than on every load.
  useEffect(() => {
    if (server === null || server === undefined) return;
    setLocal(current => (current === server ? current : server));
    writeLocal(server);
  }, [server]);
  const promptSeen = Boolean(
    data && data.preferences && data.preferences.editorPromptSeen,
  );

  const useV2 =
    override !== null
      ? override
      : (server !== null && server !== undefined ? server : local) === true;

  const setUseV2 = useCallback(
    value => {
      setLocal(value);
      setOverride(value);
      writeLocal(value);
      // Also marks the prompt seen: choosing IS having seen it, and leaving it
      // unset would show the prompt again on the next post.
      return save({
        variables: { useEditorV2: value, editorPromptSeen: true },
      })
        .then(() => refetch())
        .catch(() => {
          // The local mirror already applied, so the author is not stuck on the
          // wrong editor because of a failed write.
        });
    },
    [save, refetch],
  );

  const [promptDismissed, setPromptDismissed] = useState(false);

  const dismissPrompt = useCallback(() => {
    // Closed locally before the write goes out, because `shouldPrompt` is the
    // dialog's only way out: derived from the server alone, a failed mutation
    // left a modal with no working close button. The write still happens; if it
    // genuinely failed the prompt comes back next session, which is the
    // harmless direction to fail in.
    setPromptDismissed(true);
    return save({ variables: { editorPromptSeen: true } })
      .then(() => refetch())
      .catch(() => {});
  }, [save, refetch]);

  return {
    useV2,
    // Only offer the switch to accounts that have never answered, and only once
    // the server has actually told us so — otherwise it flashes on every load.
    shouldPrompt:
      !promptDismissed &&
      !loading &&
      Boolean(data && data.preferences) &&
      !promptSeen &&
      (server === null || server === undefined) &&
      override === null,
    loading,
    setUseV2,
    dismissPrompt,
  };
}
